import { useEffect, useState } from 'react'
import { Button, Form, FormLabel } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'

import AutoComplete from '@/components/fields/AutoComplete'
import { commercialCombinedFormStore } from '@/store/CommercialCombinedFormStore'
import { motorTradeFormStore } from '@/store/MotorTradeFormStore'
import DummyFormCC1 from '@/mock/dummyFormData.json'
import DummyFormMT1 from '@/mock/dummyFormDataMT.json'
import DummyFormMT2 from '@/mock/dummyFormDataMT2.json'
import { CommercialCombinedGuidConverter } from '@/managers/CommercialCombinedGuidConvert'
import { MotorTradeGuidConvert } from '@/managers/MotorTradeGuidConvert'
import { useRouterArea } from '@/common/auth/userRouterArea'
import { BrokerBuySteps } from '@/enums/brokerBuySteps'
import { MotorTradeBuySteps } from '@/enums/motorTradeBuySteps'

const DebugContainer = () => {
  const { setData: setCCData, setStep: setCCStep } =
    commercialCombinedFormStore()
  const { setData: setMTData, setStep: setMTStep } = motorTradeFormStore()
  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: { stepSelect: '1' },
  })
  const { isBackOffice, isBroker } = useRouterArea()

  const [useMT2, setUseMT2] = useState(false)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    const storedToggle = localStorage.getItem('useMT2')
    const storedStep = localStorage.getItem('selectedStep')

    if (storedToggle) {
      setUseMT2(storedToggle === 'true')
    }
    if (storedStep) {
      setValue('stepSelect', storedStep)
    }
    setInitialized(true)
  }, [setValue])

  useEffect(() => {
    if (initialized) {
      localStorage.setItem('useMT2', useMT2.toString())
    }
  }, [useMT2, initialized])

  useEffect(() => {
    if (initialized) {
      const currentStep = getValues('stepSelect')
      localStorage.setItem('selectedStep', currentStep)
    }
  }, [getValues, initialized])

  const isCommercialCombined = window.location.pathname.includes(
    '/commercial-combined/'
  )
  const isMotorTrade = window.location.pathname.includes(
    '/motortrade-roadrisks/'
  )

  const setMockData = () => {
    if (isCommercialCombined) {
      setCCData(CommercialCombinedGuidConverter(DummyFormCC1))
    } else if (isMotorTrade) {
      const selectedData = useMT2 ? DummyFormMT2 : DummyFormMT1
      setMTData(MotorTradeGuidConvert(selectedData))
    }
  }

  const setMockDataAndJump = (data: any) => {
    setMockData()
    const selectedStep = parseInt(data.stepSelect, 10)
    localStorage.setItem('selectedStep', data.stepSelect)
    if (isCommercialCombined) {
      const stepEnumKey = `Step${selectedStep}` as keyof typeof BrokerBuySteps
      const stepEnumValue = BrokerBuySteps[stepEnumKey]
      setCCStep(stepEnumValue)
    } else if (isMotorTrade) {
      const stepEnumKey =
        `Step${selectedStep}` as keyof typeof MotorTradeBuySteps
      const stepEnumValue = MotorTradeBuySteps[stepEnumKey]
      setMTStep(stepEnumValue)
    }
    refreshPage()
  }

  const brokerBaseURL = import.meta.env.VITE_COVERX_BROKER_DASH
  const brokerDashboardURL = brokerBaseURL + '/broker/dashboard'
  const backOfficeDashboardURL = brokerBaseURL + '/back-office/dashboard'

  const clearSessionStorage = () => {
    const commercialCombinedOptions = sessionStorage.getItem(
      'commercialCombinedOptions'
    )
    const motorTradeOptions = sessionStorage.getItem('motorTradeOptions')

    sessionStorage.clear()
    localStorage.clear()

    if (commercialCombinedOptions) {
      sessionStorage.setItem(
        'commercialCombinedOptions',
        commercialCombinedOptions
      )
    }
    if (motorTradeOptions) {
      sessionStorage.setItem('motorTradeOptions', motorTradeOptions)
    }

    refreshPage()
  }

  const returnBackOffice = () => {
    if (isBackOffice) {
      clearSessionStorage()
    }
    window.location.href = backOfficeDashboardURL
  }

  const returnBroker = () => {
    if (isBroker) {
      clearSessionStorage()
    }
    window.location.href = brokerDashboardURL
  }

  const refreshPage = () => {
    window.location.reload()
  }

  if (import.meta.env.MODE === 'dev') {
    return (
      <div
        className="debug p-3 border rounded bg-light"
        style={{
          width: '150px',
          position: 'fixed',
          right: '10px',
          top: '250px',
          zIndex: 9999,
          height: 'fit-content',
        }}
      >
        <h5>Debug</h5>
        <form>
          <div className="mb-2">
            <FormLabel>Step</FormLabel>
            <Controller
              name="stepSelect"
              control={control}
              render={({ field, fieldState }) => (
                <AutoComplete
                  id="stepSelect"
                  field={field}
                  fieldState={fieldState}
                  options={Array.from(
                    { length: isCommercialCombined ? 27 : 14 },
                    (_, i) => ({
                      text: `${i + 1}`,
                      value: (i + 1).toString(),
                    })
                  )}
                />
              )}
            />
          </div>

          <div
            className="d-flex justify-content-center mb-2"
            style={{ fontSize: '1.5rem' }}
          >
            <Form.Check
              className="form-switch"
              type="switch"
              id="caseToggle"
              checked={useMT2}
              onChange={() => setUseMT2(!useMT2)}
            />
          </div>

          <Button
            onClick={handleSubmit(setMockDataAndJump)}
            className="btn btn-success btn-block w-100 mb-2"
          >
            Set
          </Button>
        </form>

        <Button
          className="btn btn-danger btn-block w-100 mb-2"
          onClick={clearSessionStorage}
        >
          Clear
        </Button>
        <Button
          className="btn btn-warning btn-block w-100 mb-2"
          onClick={returnBroker}
        >
          Broker
        </Button>
        <Button
          className="btn btn-warning btn-block w-100 mb-2"
          onClick={returnBackOffice}
        >
          Back Office
        </Button>
      </div>
    )
  }
  return null
}

export default DebugContainer
